import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useAuthStore } from '@/lib/auth.store';
import { couponApi, type UserCoupon } from '@/lib/stage4.api';

const STATUS_LABEL = {
  unused: '可用',
  used: '已使用',
  expired: '已过期',
  revoked: '已作废',
} as const;

export function CouponsPage() {
  const user = useAuthStore((s) => s.user);
  const [list, setList] = useState<UserCoupon[]>([]);
  const [tab, setTab] = useState<'unused' | 'used' | 'expired'>('unused');
  const [code, setCode] = useState('');
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    void couponApi
      .listMine(tab)
      .then(setList)
      .catch(() => setList([]));
  }, [user, tab]);

  const claim = async () => {
    if (!code.trim()) return;
    try {
      await couponApi.claim(code.trim());
      setHint('领取成功');
      setCode('');
      const fresh = await couponApi.listMine(tab);
      setList(fresh);
    } catch (err) {
      setHint((err as Error).message);
    }
  };

  if (!user) {
    return (
      <main className="flex flex-1 items-center justify-center px-4 sm:px-6">
        <Card className="max-w-md p-6 text-center sm:p-10">
          <p className="font-serif text-2xl text-fg mb-3">优惠券</p>
          <p className="text-sm text-fg-2 mb-6">登录后查看你的优惠券</p>
          <Link to="/auth/login">
            <Button size="lg">登录</Button>
          </Link>
        </Card>
      </main>
    );
  }

  return (
    <main className="flex-1 px-4 py-8 sm:px-6 sm:py-12">
      <div className="max-w-3xl mx-auto space-y-6">
        <h1 className="font-serif text-3xl text-fg">我的优惠券</h1>

        <Card className="p-5 flex gap-3 items-center">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="输入兑换码（如 birthday_2026）"
            className="flex-1 bg-bg-1 border border-border rounded px-3 py-2 text-sm text-fg"
          />
          <Button onClick={claim}>领取</Button>
          {hint && <span className="text-xs text-fg-muted">{hint}</span>}
        </Card>

        <div className="flex gap-2 border-b border-border">
          {(['unused', 'used', 'expired'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={
                'px-4 py-2 text-sm border-b-2 -mb-px transition-colors duration-fast ' +
                (tab === t
                  ? 'border-accent text-accent'
                  : 'border-transparent text-fg-2 hover:text-fg')
              }
            >
              {STATUS_LABEL[t]}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {list.length === 0 && (
            <p className="text-sm text-fg-muted text-center py-12">暂无{STATUS_LABEL[tab]}的券</p>
          )}
          {list.map((uc) => (
            <Card key={uc.id} className="p-5 flex items-center justify-between">
              <div>
                <p className="text-fg font-medium">{uc.coupon.name}</p>
                <p className="text-xs text-fg-muted mt-1">
                  {discountText(uc.coupon)}
                  {uc.coupon.scopeProductCodes?.length > 0 && (
                    <span className="ml-2">仅限 {uc.coupon.scopeProductCodes.join(' / ')}</span>
                  )}
                </p>
                {uc.expireAt && uc.status === 'unused' && (
                  <p className="text-xs text-fg-muted mt-1">
                    {new Date(uc.expireAt).toLocaleDateString()} 到期
                  </p>
                )}
              </div>
              <div className="text-xs text-accent font-mono tracking-widest">{uc.couponCode}</div>
            </Card>
          ))}
        </div>
      </div>
    </main>
  );
}

function discountText(c: UserCoupon['coupon']): string {
  if (c.discountKind === 'percent_off') return `${c.value}% off（打 ${100 - c.value} 折）`;
  if (c.discountKind === 'amount_off') return `减 ¥${c.value}`;
  return `一口价 ¥${c.value}`;
}
